import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Phone, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import PhotoBackdrop from "@/components/PhotoBackdrop";
import Breadcrumb from "@/components/Breadcrumb";
import CTABanner from "@/components/CTABanner";
import FAQAccordion from "@/components/FAQAccordion";
import RelatedBlock from "@/components/RelatedBlock";
import ServiceCard from "@/components/cards/ServiceCard";
import IndustryCard from "@/components/cards/IndustryCard";
import ServiceSchema from "@/components/schemas/ServiceSchema";
import { COMPANY } from "@/data/company";
import { INDUSTRIES, ALL_SERVICES, REGIONS } from "@/data/nav";
import { trackPhoneClick } from "@/lib/track";

const findService = (slug) => ALL_SERVICES.find((s) => s.slug === slug);
const findIndustry = (slug) => INDUSTRIES.find((i) => i.slug === slug);

const ServiceLeafTemplate = ({ data }) => {
  const industries = data.industries
    .map((i) => findIndustry(i.slug) || i)
    .filter(Boolean);
  const related = data.related
    .map((slug) => findService(slug))
    .filter(Boolean);

  return (
    <div data-testid={`service-page-${data.slug}`}>
      <Seo
        title={data.seo.title}
        description={data.seo.description}
        path={`/services/${data.slug}`}
      />
      <ServiceSchema
        slug={data.slug}
        name={data.h1?.replace(/\.$/, "") || data.seo.title}
        description={data.seo.description}
        categoryName={data.category?.name}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <PhotoBackdrop src={data.heroImage} position={data.heroPosition} />
        <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-20 lg:pt-28 pb-16 relative">
          <p className="eyebrow">{data.category.name} · Service</p>
          <h1
            className="font-display mt-6 text-white max-w-4xl"
            style={{ fontSize: "clamp(36px, 5vw, 68px)", lineHeight: 1 }}
            data-testid="service-h1"
          >
            {data.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-[16px] leading-relaxed text-[color:var(--lt-text-dim)]">
            {data.subhead}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href={COMPANY.phone.tel}
              onClick={() => trackPhoneClick(`service_${data.slug}_hero`)}
              className="btn-primary inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="service-hero-call"
            >
              <Phone size={16} /> Call an engineer
            </a>
            <Link
              to="/contact"
              className="btn-ghost inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold"
              data-testid="service-hero-enquire"
            >
              Send an enquiry <ArrowRight size={15} />
            </Link>
          </div>

          <div className="mt-10 flex flex-wrap gap-2">
            {data.responseChips.map((c) => (
              <span
                key={c}
                className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em]"
                style={{
                  background: "rgba(58, 108, 255, 0.12)",
                  border: "1px solid rgba(127, 166, 255, 0.35)",
                  color: "#c9d8ff",
                }}
              >
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      <Breadcrumb
        items={[
          { label: "Services", to: "/services" },
          { label: data.category.name, to: `/services` },
          { label: data.h1.replace(/[.,].*$/, "") },
        ]}
      />

      {/* WHAT WE DO */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="eyebrow">What we do</p>
          <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
            The service in plain English.
          </h2>
        </div>
        <div className="lg:col-span-7">
          <p className="text-[15.5px] leading-relaxed text-[color:var(--lt-text-dim)] whitespace-pre-line">
            {data.what}
          </p>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-20">
        <p className="eyebrow">What&apos;s included</p>
        <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
          Every job covers the following.
        </h2>
        <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.included.map((item) => (
            <li key={item} className="glass-card rounded-lg p-4 flex items-start gap-3">
              <CheckCircle2 size={18} className="mt-0.5 text-[color:var(--lt-blue-bright)] flex-shrink-0" />
              <span className="text-[14px] leading-relaxed text-[color:var(--lt-text)]">{item}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* WHO IT'S FOR */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Who it&apos;s for</p>
        <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
          Common on these sites.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {industries.map((i) => (
            <IndustryCard key={i.slug} slug={i.slug} name={i.name} blurb={i.blurb} />
          ))}
        </div>
      </section>

      {/* OUR APPROACH */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Our approach</p>
        <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
          Survey. Quote. Deliver. Sign off.
        </h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.approach.map((s) => (
            <div key={s.step} className="glass-card rounded-xl p-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[color:var(--lt-blue-soft)]">
                {s.step}
              </p>
              <h3 className="font-display mt-3 text-white text-xl">{s.title}</h3>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[color:var(--lt-text-dim)]">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="glass-card rounded-xl p-8 lg:p-10 max-w-4xl">
          <p className="eyebrow">How we price this work</p>
          <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(24px, 2.8vw, 34px)", lineHeight: 1.05 }}>
            No surprise invoices.
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">{data.pricing}</p>
        </div>
      </section>

      {/* COVERAGE CALLOUT */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <p className="eyebrow">Coverage</p>
        <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
          Where we deliver this service.
        </h2>
        <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-[color:var(--lt-text-dim)]">
          We deliver {data.h1.toLowerCase().replace(/[.,].*$/, "")} across the North West of England — from our
          Northwich base. Same-day dispatch for callouts and scheduled visits for planned work.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {REGIONS.map((r) => (
            <Link
              key={r.slug}
              to={`/coverage/${r.slug}`}
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[13px] font-semibold text-white transition-colors"
              style={{
                background: "rgba(15, 26, 51, 0.6)",
                border: "1px solid var(--lt-border-strong)",
              }}
              data-testid={`service-coverage-${r.slug}`}
            >
              {r.name}
            </Link>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-7xl mx-auto px-5 lg:px-10 mt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="eyebrow">Service FAQ</p>
            <h2 className="font-display mt-4 text-white" style={{ fontSize: "clamp(28px, 3.4vw, 42px)", lineHeight: 1 }}>
              What clients ask before they call.
            </h2>
          </div>
          <div className="lg:col-span-8">
            <FAQAccordion items={data.faq} testIdPrefix={`service-faq-${data.slug}`} />
          </div>
        </div>
      </section>

      {/* CTA BANNER — variant conditional on service urgency */}
      <CTABanner variant={data.emergencyCta ? "emergency" : "standard"} phoneLocation={`service_${data.slug}_bottom`} />

      {/* RELATED */}
      <RelatedBlock
        title="Adjacent services you might also need"
        groups={[
          {
            eyebrow: "Related services",
            items: related.map((s) => (
              <ServiceCard key={s.slug} slug={s.slug} name={s.name} blurb={s.blurb} categoryName={s.categoryName} />
            )),
          },
        ]}
      />
    </div>
  );
};

export default ServiceLeafTemplate;
